/**
 * Wrapper around FoxTheme.Motion.animate that avoids a one-frame flash when an
 * animation ends.
 *
 * Motion cancels the finished WAAPI animation synchronously but only writes the
 * final values to the inline style on the next frame. For that one frame the
 * element falls back to its previous inline style (the hidden pre-animation
 * state), so it blinks out. onComplete runs right before the cancel, so the
 * final values are committed there.
 *
 * A zero-duration animation (the pre-animation state) is committed right away
 * as well: Motion applies it on the next frame, and by then the
 * `motion-element:not(:defined)` / `grid-list:not(:defined)` guards in
 * critical-css.liquid have already stopped matching, so the element would
 * paint visible in between.
 *
 * A NodeList is animated element by element, because onComplete does not say
 * which element finished and a staggered group must not commit early.
 */
function commitFinalStyles(element, keyframes) {
  Object.entries(keyframes).forEach(([property, value]) => {
    element.style[property] = Array.isArray(value)
      ? value[value.length - 1]
      : value;
  });
}

function animateMotion(target, keyframes, options = {}) {
  if (!(target instanceof Element)) {
    const elements = Array.from(target);
    return elements.map((element, index) =>
      animateMotion(element, keyframes, {
        ...options,
        delay:
          typeof options.delay === "function"
            ? options.delay(index, elements.length)
            : options.delay,
      })
    );
  }

  if (options.duration === 0) commitFinalStyles(target, keyframes);

  return FoxTheme.Motion.animate(target, keyframes, {
    ...options,
    onComplete: () => commitFinalStyles(target, keyframes),
  });
}

class MotionElement extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (FoxThemeSettings.motionReduced) return;

    this.preInitialize();
    FoxTheme.Motion.inView(
      this,
      async () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.initialize();
          });
        });
      },
      { margin: "0px 0px -50px 0px" }
    );
  }

  get isHold() {
    return this.hasAttribute("hold");
  }

  get animationType() {
    return this.dataset.motion || "none";
  }

  get animationDelay() {
    return parseInt(this.dataset.motionDelay || 0) / 1000;
  }

  /**
   * Value in rem
   */
  getTranslateX(value) {
    if (FoxThemeSettings.isRTL) {
      value *= -1;
    }

    return `${value}rem`;
  }

  preInitialize() {
    if (this.isHold) return;

    switch (this.animationType) {
      case "fade-in":
        animateMotion(this, { opacity: 0.01 }, { duration: 0 });
        break;

      case "fade-in-up":
        animateMotion(
          this,
          { transform: "translateY(2rem)", opacity: 0.01 },
          { duration: 0 }
        );
        break;

      case "fade-in-left":
        animateMotion(
          this,
          {
            transform: `translateX(${this.getTranslateX(-2)})`,
            opacity: 0.01,
          },
          { duration: 0 }
        );
        break;

      case "fade-in-right":
        animateMotion(
          this,
          {
            transform: `translateX(${this.getTranslateX(2)})`,
            opacity: 0.01,
          },
          { duration: 0 }
        );
        break;

      case "zoom-in-lg":
        animateMotion(
          this,
          { transform: "scale(0)" },
          { duration: 0 }
        );
        break;

      case "zoom-out-sm":
        animateMotion(
          this,
          { transform: "scale(1.1)" },
          { duration: 0 }
        );
        break;
    }
  }

  async initialize() {
    if (this.isHold) return;

    switch (this.animationType) {
      case "fade-in":
        await animateMotion(
          this,
          { opacity: 1 },
          { duration: 1.5, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
        ).finished;
        break;

      case "fade-in-up":
      case "fade-in-left":
      case "fade-in-right":
        await animateMotion(
          this,
          { transform: "translate(0, 0)", opacity: 1 },
          {
            duration: FoxThemeSettings.animationDuration,
            delay: this.animationDelay,
            easing: [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case "zoom-in-lg":
      case "zoom-out-sm":
        await animateMotion(
          this,
          { transform: "scale(1)" },
          {
            duration: FoxThemeSettings.animationDuration,
            delay: this.animationDelay,
            easing: [0, 0, 0.3, 1],
          }
        ).finished;
        break;
    }
  }

  async resetAnimation(duration) {
    switch (this.animationType) {
      case "fade-in":
        await animateMotion(
          this,
          { opacity: 0 },
          {
            duration: duration ? duration : 1.5,
            delay: this.animationDelay,
            easing: duration ? "none" : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case "fade-in-up":
        await animateMotion(
          this,
          { transform: "translateY(2rem)", opacity: 0 },
          {
            duration: duration ? duration : FoxThemeSettings.animationDuration,
            delay: this.animationDelay,
            easing: duration ? "none" : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case "fade-in-left":
        await animateMotion(
          this,
          {
            transform: `translateX(${this.getTranslateX(-2)})`,
            opacity: 0,
          },
          {
            duration: duration ? duration : FoxThemeSettings.animationDuration,
            delay: this.animationDelay,
            easing: duration ? "none" : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case "fade-in-right":
        await animateMotion(
          this,
          {
            transform: `translateX(${this.getTranslateX(2)})`,
            opacity: 0,
          },
          {
            duration: duration ? duration : FoxThemeSettings.animationDuration,
            delay: this.animationDelay,
            easing: duration ? "none" : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case "zoom-in-lg":
        await animateMotion(
          this,
          { transform: "scale(0)" },
          {
            duration: duration ? duration : 1.3,
            delay: this.animationDelay,
            easing: duration ? "none" : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case "zoom-out-sm":
        await animateMotion(
          this,
          { transform: "scale(0)" },
          {
            duration: duration ? duration : 1.3,
            delay: this.animationDelay,
            easing: duration ? "none" : [0.16, 1, 0.3, 1],
          }
        ).finished;
        break;
    }
  }

  refreshAnimation() {
    this.removeAttribute("hold");
    this.preInitialize();
    setTimeout(() => {
      this.initialize();
    }, 50); // Delay a bit to make animation re init properly.
  }
}
customElements.define("motion-element", MotionElement);

class GridList extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (FoxThemeSettings.motionReduced) return;

    this.hideGridItems();
    FoxTheme.Motion.inView(
      this,
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.showGridItems();
          });
        });
      },
      { margin: "0px 0px -50px 0px" }
    );
  }

  get animationType() {
    return this.dataset.motion || "none";
  }

  get gridItems() {
    return this.querySelectorAll(".grid-list__column");
  }

  get visibleGridItems() {
    return this.querySelectorAll(".grid-list__column:not([style])");
  }

  /**
   * Value in rem
   */
  getTranslateX(value) {
    if (FoxThemeSettings.isRTL) {
      value *= -1;
    }

    return `${value}rem`;
  }

  hideGridItems() {
    switch (this.animationType) {
      case "fade-in":
        animateMotion(
          this.gridItems,
          { opacity: 0.01 },
          { duration: 0 }
        );
        break;

      case "fade-in-up":
        animateMotion(
          this.gridItems,
          {
            transform: "translateY(2rem)",
            opacity: 0.01,
            visibility: "hidden",
          },
          { duration: 0 }
        );
        break;

      case "fade-in-left":
        animateMotion(
          this.gridItems,
          {
            transform: `translateX(${this.getTranslateX(-2)})`,
            opacity: 0.01,
            visibility: "hidden",
          },
          { duration: 0 }
        );
        break;

      case "fade-in-right":
        animateMotion(
          this.gridItems,
          {
            transform: `translateX(${this.getTranslateX(2)})`,
            opacity: 0.01,
            visibility: "hidden",
          },
          { duration: 0 }
        );
        break;
    }
  }

  showItems(items) {
    switch (this.animationType) {
      case "fade-in":
        animateMotion(
          items,
          {
            opacity: [0.01, 1],
            visibility: ["hidden", "visible"],
          },
          {
            duration: FoxThemeSettings.animationDuration,
            delay: FoxTheme.Motion.stagger(0.1),
            easing: [0, 0, 0.3, 1],
          }
        );
        break;

      case "fade-in-up":
        animateMotion(
          items,
          {
            transform: ["translateY(2rem)", "translateY(0rem)"],
            opacity: [0.01, 1],
            visibility: ["hidden", "visible"],
          },
          {
            duration: FoxThemeSettings.animationDuration,
            delay: FoxTheme.Motion.stagger(0.1),
            easing: [0, 0, 0.3, 1],
          }
        );
        break;

      case "fade-in-left":
        animateMotion(
          items,
          {
            transform: [
              `translateX(${this.getTranslateX(-2)})`,
              "translateX(0rem)",
            ],
            opacity: [0.01, 1],
            visibility: ["hidden", "visible"],
          },
          {
            duration: FoxThemeSettings.animationDuration,
            delay: FoxTheme.Motion.stagger(0.1),
            easing: [0, 0, 0.3, 1],
          }
        );
        break;

      case "fade-in-right":
        animateMotion(
          items,
          {
            transform: [
              `translateX(${this.getTranslateX(2)})`,
              "translateX(0rem)",
            ],
            opacity: [0.01, 1],
            visibility: ["hidden", "visible"],
          },
          {
            duration: FoxThemeSettings.animationDuration,
            delay: FoxTheme.Motion.stagger(0.1),
            easing: [0, 0, 0.3, 1],
          }
        );
        break;
    }
  }

  showGridItems() {
    this.showItems(this.gridItems);
  }

  reShowVisibleGridItems() {
    this.showItems(this.visibleGridItems);
  }
}
customElements.define("grid-list", GridList);
